import type { ClientProposal } from "./proposalTypes";

export type ProposalPrintPrefs = {
  venueName: string | null;
  dateLabel: string | null;
  entertainmentLabel: string | null;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Opens a print-ready proposal window so the client can Save as PDF. */
export function openProposalPrint(
  proposal: ClientProposal,
  prefs: ProposalPrintPrefs
): void {
  const html = buildProposalPrintHtml(proposal, prefs);
  const win = window.open("", "_blank");
  if (!win) {
    window.alert("Allow pop-ups for this site to download the proposal PDF.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  const triggerPrint = () => {
    try {
      win.print();
    } catch {
      // Client can use the browser print button if auto-print is blocked.
    }
  };
  if (win.document.readyState === "complete") {
    setTimeout(triggerPrint, 350);
  } else {
    win.addEventListener("load", () => setTimeout(triggerPrint, 350));
  }
}

export function buildProposalPrintHtml(
  proposal: ClientProposal,
  prefs: ProposalPrintPrefs
): string {
  const featured = proposal.featuredCaseStudy;
  const featuredHtml = `
    <section class="card">
      <div class="eyebrow">${escapeHtml(featured.eyebrow)}</div>
      <h3>${escapeHtml(featured.title)}</h3>
      <p class="muted">${escapeHtml(featured.venueName)} · ${escapeHtml(featured.guestCount)}</p>
      <p>${escapeHtml(featured.body)}</p>
      <p class="muted">${escapeHtml(featured.eventLabel)}</p>
    </section>`;

  const venues = proposal.venues
    .map((v) => {
      const cs = v.caseStudy
        ? `<p><strong>Case study (${escapeHtml(v.caseStudy.guestCount)}):</strong> ${escapeHtml(v.caseStudy.title)}. ${escapeHtml(v.caseStudy.body)} <span class="muted">${escapeHtml(v.caseStudy.eventLabel)}</span></p>`
        : "";
      return `
      <section class="card">
        <h3>${escapeHtml(v.name)}</h3>
        <p class="muted">${escapeHtml(v.neighborhood)} · ${escapeHtml(v.guestFit)} · ${escapeHtml(v.availability)}</p>
        <p>${escapeHtml(v.description)}</p>
        <ul>${v.highlights.map((h) => `<li>${escapeHtml(h)}</li>`).join("")}</ul>
        ${cs}
      </section>`;
    })
    .join("");

  const entertainment = proposal.entertainmentOptions
    .map(
      (opt) => `
      <section class="card">
        <div class="eyebrow">${escapeHtml(opt.tagline)}</div>
        <h3>${escapeHtml(opt.label)}</h3>
        <p>${escapeHtml(opt.description)}</p>
        <p><strong>Best for:</strong> ${escapeHtml(opt.bestFor)}</p>
        <ul>${opt.includes.map((i) => `<li>${escapeHtml(i)}</li>`).join("")}</ul>
        <p class="muted">${escapeHtml(opt.pricingNote)}</p>
      </section>`
    )
    .join("");

  const timeline = proposal.timeline
    .map(
      (step) =>
        `<tr><td class="time">${escapeHtml(step.time)}</td><td><strong>${escapeHtml(step.title)}</strong><br/><span class="muted">${escapeHtml(step.body)}</span></td></tr>`
    )
    .join("");

  const handles = proposal.handles
    .map((h) => `<li>${escapeHtml(h)}</li>`)
    .join("");

  const clients = proposal.clientLogos
    .map((logo) => `<li>${escapeHtml(logo.alt)}</li>`)
    .join("");

  const prefRows = [
    ["Preferred venue", prefs.venueName || "Not selected"],
    ["Preferred date", prefs.dateLabel || "Not selected"],
    ["Entertainment", prefs.entertainmentLabel || "Not selected"],
  ]
    .map(
      ([k, v]) =>
        `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(proposal.clientName)} · ${escapeHtml(proposal.eventTitle)}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 36px 40px 56px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      color: #1a1612;
      background: #fff;
      line-height: 1.45;
      font-size: 13px;
    }
    h1 { font-size: 28px; margin: 0 0 6px; letter-spacing: -0.02em; }
    h2 { font-size: 16px; margin: 28px 0 10px; border-bottom: 1px solid #ddd; padding-bottom: 6px; text-transform: uppercase; letter-spacing: 0.08em; }
    h3 { font-size: 15px; margin: 0 0 6px; }
    p { margin: 0 0 10px; }
    .muted { color: #666; }
    .eyebrow { text-transform: uppercase; letter-spacing: 0.14em; font-size: 11px; color: #8a7350; font-weight: 700; margin-bottom: 8px; }
    .summary { display: flex; flex-wrap: wrap; gap: 10px; margin: 16px 0 8px; }
    .chip { border: 1px solid #ddd; padding: 6px 10px; border-radius: 2px; font-size: 12px; }
    .card { border: 1px solid #e5e0d8; padding: 14px 16px; margin-bottom: 12px; border-radius: 2px; page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
    td, th { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    td.time { width: 90px; font-weight: 600; white-space: nowrap; }
    ul { margin: 6px 0 10px; padding-left: 18px; }
    li { margin-bottom: 3px; }
    .disclaimer { font-size: 11px; color: #777; margin-top: 8px; }
    .footer { margin-top: 28px; font-size: 11px; color: #888; }
    @media print {
      body { padding: 0; }
      a { color: inherit; text-decoration: none; }
    }
  </style>
</head>
<body>
  <div class="eyebrow">ComedyUO private proposal</div>
  <h1>${escapeHtml(proposal.heroHeadline)}</h1>
  <p class="muted">${escapeHtml(proposal.clientName)} · ${escapeHtml(proposal.eventTitle)}</p>
  <p>${escapeHtml(proposal.heroSubheadline)}</p>
  <div class="summary">
    ${proposal.summaryItems.map((s) => `<span class="chip">${escapeHtml(s)}</span>`).join("")}
  </div>

  <h2>Your preferences</h2>
  <table>${prefRows}</table>

  <h2>${escapeHtml(proposal.experienceTitle)}</h2>
  <p>${escapeHtml(proposal.experienceIntro)}</p>
  ${proposal.experiencePillars
    .map(
      (p) =>
        `<p><strong>${escapeHtml(p.number)}. ${escapeHtml(p.title)}</strong>. ${escapeHtml(p.body)}</p>`
    )
    .join("")}

  <h2>Case study · 50–60 guests</h2>
  ${featuredHtml}

  <h2>Venue options</h2>
  ${venues}

  <h2>${escapeHtml(proposal.entertainmentTitle)}</h2>
  <p>${escapeHtml(proposal.entertainmentIntro)}</p>
  ${entertainment}

  <h2>${escapeHtml(proposal.timelineTitle)}</h2>
  <p class="muted">${escapeHtml(proposal.timelineNote)}</p>
  <table>${timeline}</table>

  <h2>${escapeHtml(proposal.handlesTitle)}</h2>
  <ul>${handles}</ul>

  <h2>${escapeHtml(proposal.clientsTitle)}</h2>
  <p>${escapeHtml(proposal.clientsIntro)}</p>
  <ul>${clients}</ul>

  <h2>${escapeHtml(proposal.nextStepsTitle)}</h2>
  <p>${escapeHtml(proposal.nextStepsBody)}</p>

  <p class="footer">Prepared by ComedyUO · Confidential · ${escapeHtml(proposal.clientName)}</p>
</body>
</html>`;
}
